/**
 * Demo-mode plumbing.
 *
 * The PoC showcase ships a "Try the demo" entry path: visitors land on
 * `/demo`, pick a persona on a preloaded scenario, and the SPA swaps the
 * dev-shim `Bearer dev:<id>` header to act as that persona. Per
 * `docs/POC_SHOWCASE_SPEC.md` §2.1 + the engineering work order step 6.
 *
 * Demo mode flips on when ANY of the following are true:
 *
 *   1. `import.meta.env.VITE_DEMO_MODE` is `"1"` (build-time opt-in for
 *      the deployed PoC SPA).
 *   2. The host matches `*.pages.dev` or `*.fly.dev` (so preview deploys
 *      auto-flip without a rebuild).
 *   3. The user has visited `/demo` at least once in this session — we
 *      remember via `sessionStorage` so the persona/scenario switcher
 *      stays in the user pill until they sign up.
 */

const SESSION_FLAG = "tarii.demo_mode";
const SCENARIO_KEY = "tarii.demo_scenario";
/**
 * Persistent (across-session) record of the last scenario the visitor
 * picked on `/demo`. Distinct from `SCENARIO_KEY` (session-scoped, drives
 * the active dev-shim auth) — `LAST_SCENARIO_KEY` is the "what should we
 * pre-select for this returning visitor?" hint. Closes doctor-persona
 * walkthrough §9 friction F5: a first-time visitor with no `last_scenario`
 * gets `clinical_first_run` auto-picked; a returning visitor lands back
 * on whatever they last opened. Picker remains explicit — clicking any
 * card overrides + writes this key.
 */
const LAST_SCENARIO_KEY = "tarii.demo.last_scenario";

/** Default scenario auto-selected for first-time visitors with no
 *  `LAST_SCENARIO_KEY` in localStorage. `clinical_first_run` is the
 *  empty-workspace onboarding scenario — the cleanest entry point for
 *  someone who has never seen Tarii before. Per F5 walkthrough notes
 *  (`docs/audits/doctor-persona-walkthrough.md` §2 + §9). */
export const DEFAULT_DEMO_SCENARIO = "clinical_first_run";

function hostMatchesPreview(host: string): boolean {
  return /\.pages\.dev$/i.test(host) || /\.fly\.dev$/i.test(host);
}

export function isDemoMode(): boolean {
  if (typeof window === "undefined") return false;
  if (import.meta.env.VITE_DEMO_MODE === "1") return true;
  if (window.sessionStorage.getItem(SESSION_FLAG) === "1") return true;
  return hostMatchesPreview(window.location.host);
}

export function setDemoMode(enabled: boolean): void {
  if (typeof window === "undefined") return;
  if (enabled) {
    window.sessionStorage.setItem(SESSION_FLAG, "1");
  } else {
    window.sessionStorage.removeItem(SESSION_FLAG);
    window.sessionStorage.removeItem(SCENARIO_KEY);
  }
}

export function getActiveScenario(): string | null {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(SCENARIO_KEY);
}

export function setActiveScenario(name: string | null): void {
  if (typeof window === "undefined") return;
  if (name) {
    window.sessionStorage.setItem(SCENARIO_KEY, name);
  } else {
    window.sessionStorage.removeItem(SCENARIO_KEY);
  }
}

/** Read the "last scenario the visitor picked" hint. Returns `null` for
 *  first-time visitors (no localStorage entry); the picker treats `null`
 *  as a signal to fall back to `DEFAULT_DEMO_SCENARIO`. */
export function getLastScenario(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(LAST_SCENARIO_KEY);
  } catch {
    // localStorage can throw in private-browsing or storage-disabled
    // contexts; failing closed (= treat as first-time visitor) is the
    // safe default — the page still renders with the auto-pick.
    return null;
  }
}

/** Persist the "last scenario" hint. Called on every picker click so a
 *  returning visitor lands back on whatever card they last opened. */
export function setLastScenario(name: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LAST_SCENARIO_KEY, name);
  } catch {
    // Same reasoning as `getLastScenario`: localStorage failure is not
    // fatal — the active session still works via `SCENARIO_KEY`.
  }
}

// ─── Schema for `GET /api/dev/scenarios` ─────────────────────────────
// Hand-rolled until the next `pnpm gen:types` run picks up the backend
// addition. Mirrors `backend/src/api/routes/dev.py:DevScenario`.

export interface DevScenarioMember {
  display_name: string;
  tier: string;
  role: string;
}

export type DemoPlan = "free" | "pro" | "team";

export interface DevScenario {
  name: string;
  label: string;
  /** One-line tagline rendered in the picker card header. */
  description: string;
  /** Paragraph-length narrative — what the scenario demonstrates
   *  end-to-end. Rendered below `description` on the `/demo` card so a
   *  watcher knows the story before clicking in. Backend-added by the
   *  2026-04-30 scenario revamp; older deploys may omit it. */
  story?: string;
  /** 1-3 short orientation prompts ("Open the X tab", "Switch to
   *  persona Y") for the demo-watcher's first move on the scenario.
   *  Backend-added by the 2026-04-30 scenario revamp. */
  try_this?: string[];
  team_id: string | null;
  team_name: string;
  cycle_id: string | null;
  cycle_name: string;
  account_id: string;
  /** The tier the scenario's lead persona is on after a reset. Drives
   *  the Free/Pro/Team badge in the demo picker + the active-persona
   *  pill so prospects see *what they'd get* per tier. */
  account_plan: DemoPlan;
  members_summary: DevScenarioMember[];
  loaded: boolean;
}

export interface DevScenarioResetResponse {
  name: string;
  team_id: string;
  team_name: string;
  cycle_id: string;
  members_loaded: number;
  account_plan: DemoPlan;
}
